import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types'
import type { ModuleSettings } from '@/stores/auth/state'

export interface RouteMeta {
  requiresAuth?: boolean
  requiresGuest?: boolean
  roles?: UserRole[]
  // Módulo del tenant requerido para entrar a la ruta. El superadmin siempre pasa.
  requiresModule?: keyof ModuleSettings
  title?: string
}

export async function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const authStore = useAuthStore()

  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.initializeAuth()
  }

  const meta = to.meta as RouteMeta
  const isAuthenticated = authStore.isAuthenticated
  const userRole = authStore.userRole

  // Check if route requires authentication
  if (meta.requiresAuth && !isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // Check if route is for guests only (login, register)
  if (meta.requiresGuest && isAuthenticated) {
    next({ path: '/dashboard' })
    return
  }

  // Forzar cambio de la clave TEMPORAL antes de usar la app (mustChangePassword).
  if (isAuthenticated && authStore.user?.mustChangePassword && to.path !== '/force-change-password') {
    next({ path: '/force-change-password' })
    return
  }
  if (isAuthenticated && !authStore.user?.mustChangePassword && to.path === '/force-change-password') {
    next({ path: '/dashboard' })
    return
  }

  // Check if route requires specific roles
  // Superadmin always has access to all routes
  if (meta.roles && meta.roles.length > 0) {
    if (userRole === 'superadmin') {
      next()
      return
    }
    if (!userRole || !meta.roles.includes(userRole)) {
      next({ path: '/unauthorized' })
      return
    }
  }

  // Check if route requires a tenant module to be enabled.
  // Superadmin bypasses the module flag (ve todo).
  if (meta.requiresModule && userRole !== 'superadmin') {
    if (!authStore.isModuleEnabled(meta.requiresModule)) {
      next({ path: '/unauthorized' })
      return
    }
  }

  next()
}

export function roleGuard(allowedRoles: UserRole[]) {
  return (
    _to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): void => {
    const authStore = useAuthStore()
    const userRole = authStore.userRole

    if (!userRole || !allowedRoles.includes(userRole)) {
      next({ path: '/unauthorized' })
      return
    }

    next()
  }
}

export function adminGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isSuperAdmin && !authStore.isTenantAdmin && !authStore.isAdmin) {
    next({ path: '/unauthorized' })
    return
  }

  next()
}

export function operatorGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isSuperAdmin && !authStore.isTenantAdmin && !authStore.isAdmin && !authStore.isOperator) {
    next({ path: '/unauthorized' })
    return
  }

  next()
}

export function superAdminGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isSuperAdmin) {
    next({ path: '/unauthorized' })
    return
  }

  next()
}

export function clientGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()
  const role = authStore.userRole

  if (role !== 'client' && role !== 'member') {
    next({ path: '/unauthorized' })
    return
  }

  next()
}

export function organizationOwnerGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore()

  if (!authStore.isOrganizationOwner && !authStore.isSuperAdmin && !authStore.isTenantAdmin && !authStore.isAdmin) {
    next({ path: '/unauthorized' })
    return
  }

  next()
}
